/* global React */
/* ──────────────────────────────────────────────────────────────────────
   CtaStrip — "Start Your Project" conversion section, shared across every
   page. Centered title + lead, 3-step "what happens next" + stats column
   on the left, inquiry form on the right.

   The form itself (DzInquiryForm) is the one inquiry form of the site: the
   Contact page renders the same component, so every page sends the same
   fields to the same place.

   Reads its data from window.DZ_HOME.ctaStrip (content/home.js).
   ────────────────────────────────────────────────────────────────────── */
const { useEffect: useEffectCta, useRef: useRefCta, useState: useStateCta } = React;

// Ad-source keys captured once per visit and sent with every inquiry.
const CTA_SRC_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'li_fat_id'];

// Remember where the visitor came from (first landing page + ad parameters)
// in sessionStorage, so a lead sent later in the same visit keeps its source.
function ctaLeadSource() {
  let store = {};
  try { store = JSON.parse(sessionStorage.getItem('dz_src') || '{}'); } catch (e) { store = {}; }
  const qs = new URLSearchParams(window.location.search);
  CTA_SRC_KEYS.forEach((k) => { if (qs.get(k)) store[k] = qs.get(k); });
  if (!store.landing) store.landing = window.location.pathname;
  if (!store.referrer && document.referrer) store.referrer = document.referrer;
  try { sessionStorage.setItem('dz_src', JSON.stringify(store)); } catch (e) { /* private mode */ }
  return store;
}

// Page → project-type key (matches data.form.projectTypeKeys). ?topic= on the
// Contact page wins, so "/contact/?topic=twinmaq" arrives with TwinMaq chosen.
const CTA_PAGE_KEY = {
  bim: 'bim', 'arch-bim': 'bim', 'mep-bim': 'bim', 'clash-detection': 'bim', 'cobie-cafm': 'bim',
  'iso-19650': 'iso', 'vision-2030': 'bim', services: 'bim', twinms: 'twinms', twinmaq: 'twinmaq', 'scan-to-bim': 'hbim', hbim: 'hbim', vdc: 'vdc'
};
function ctaPageKey() {
  const topic = (new URLSearchParams(window.location.search).get('topic') || '').toLowerCase();
  if (topic) return CTA_PAGE_KEY[topic] || topic;
  if (window.DZ_CTA_KEY) return window.DZ_CTA_KEY; // a page can pre-select a service (e.g. the digital twin guide → twinms)
  const seg = window.location.pathname.replace(/^\/ar(?=\/)/, '').split('/').filter(Boolean)[0] || 'home';
  return CTA_PAGE_KEY[seg] || seg;
}
// Project-type key / page → the service name the CRM uses.
const CTA_CRM_SERVICE = { bim: 'BIM', iso: 'BIM', hbim: 'BIM', twinms: 'TwinMS', twinmaq: 'TwinMaq', vdc: 'VDC', ai: 'AI' };

// Every lead goes to Netlify Forms and to the company CRM (leads.designzone.com.sa).
// window.DZ_LEADS_ENDPOINT can override the CRM address (e.g. for a test CRM).
function ctaCrmEndpoint() {
  return window.DZ_LEADS_ENDPOINT || 'https://leads.designzone.com.sa/api/intake';
}

const CTA_ARROW = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CTA_LOCK = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 11V7a7 7 0 0 0-14 0v4" strokeLinecap="round" />
    <rect x="5" y="11" width="14" height="10" rx="2" strokeLinecap="round" />
  </svg>
);

// Page-specific wording (e.g. the TwinMaq demo form) layered over the shared copy in content/home.js.
function ctaData() {
  const base = window.DZ_HOME.ctaStrip;
  const ar = document.documentElement.lang === 'ar';
  const seg = window.location.pathname.replace(/^\/ar(?=\/)/, '').split('/').filter(Boolean)[0] || 'home';
  const byPath = (ar ? window.DZ_PAGE_CTA_BY_PATH_AR : window.DZ_PAGE_CTA_BY_PATH) || {};
  const page = (ar ? window.DZ_PAGE_CTA_AR : window.DZ_PAGE_CTA) || byPath[seg] || null;
  if (!page) return base;
  return Object.assign({}, base, page, { form: Object.assign({}, base.form, page.form || {}) });
}

function DzInquiryForm({ idPrefix = 'cta' }) {
  const f = ctaData().form;
  const keys = f.projectTypeKeys || [];
  const pageKey = ctaPageKey();
  const preset = keys.indexOf(pageKey);
  // Submit state: idle | submitting | success | error
  const [status, setStatus] = useStateCta('idle');
  const id = (n) => idPrefix + '-' + n;

  useEffectCta(() => { ctaLeadSource(); }, []);

  // Sends to Netlify Forms (form "dz-inquiry", registered by the hidden copy
  // in /index.html) and to the CRM in parallel. Success if either accepts.
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (status === 'submitting') return;
    const form = event.currentTarget;
    const fd = new FormData(form);
    if ((fd.get('bot-field') || '').toString().trim() !== '') { setStatus('success'); form.reset(); return; }

    const lang = document.documentElement.lang === 'ar' ? 'ar' : 'en';
    const typeIdx = f.projectTypes.indexOf((fd.get('project_type') || '').toString());
    const typeKey = typeIdx >= 0 ? keys[typeIdx] : '';
    const chosen = typeKey && typeKey !== 'other';
    const service = CTA_CRM_SERVICE[chosen ? typeKey : pageKey] || 'Not sure';
    const src = ctaLeadSource();
    fd.set('form-name', 'dz-inquiry');
    fd.set('service', service);
    fd.set('lang', lang);
    fd.set('page', window.location.origin + window.location.pathname);
    fd.set('subject', '[' + service.toUpperCase() + '] ' + (fd.get('name') || 'New inquiry'));
    ['landing', 'referrer'].concat(CTA_SRC_KEYS).forEach((k) => { if (src[k]) fd.set(k, src[k]); });

    const body = new URLSearchParams();
    fd.forEach((value, key) => body.append(key, value.toString()));
    const lead = {};
    fd.forEach((value, key) => { if (key !== 'project_type') lead[key] = value.toString(); });
    lead.service_detail = (fd.get('project_type') || '').toString();
    lead.service_source = chosen ? 'visitor' : 'page';
    lead.form = 'dz-inquiry';
    lead.device = window.matchMedia && window.matchMedia('(max-width: 800px)').matches ? 'mobile' : 'desktop';

    setStatus('submitting');
    const crm = ctaCrmEndpoint();
    const sends = [fetch('/', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() })
      .then((r) => { if (!r.ok) throw new Error('Netlify HTTP ' + r.status); })];
    if (crm) sends.push(fetch(crm, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(lead) })
      .then((r) => { if (!r.ok) throw new Error('CRM HTTP ' + r.status); }));
    const results = await Promise.allSettled(sends);
    if (results.some((r) => r.status === 'fulfilled')) {
      setStatus('success');
      form.reset();
      // Conversion event for Google Analytics (and Tag Manager when added).
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'generate_lead', form: 'dz-inquiry', service: service, page_lang: lang });
      if (typeof window.gtag === 'function') window.gtag('event', 'generate_lead', { form_name: 'dz-inquiry', service: service, language: lang });
      // Google Ads lead conversion with enhanced-conversion data (js/dz-ads.js),
      // then the thank-you page in the visitor's language.
      const goThanks = () => window.location.assign(lang === 'ar' ? '/ar/thank-you/' : '/thank-you/');
      if (typeof window.DZ_trackLead === 'function') window.DZ_trackLead({ email: (fd.get('email') || '').toString(), phone: (fd.get('phone') || '').toString() }, goThanks);
      else goThanks();
    } else {
      // eslint-disable-next-line no-console
      results.forEach((r) => console.error('Inquiry form submit failed:', r.reason));
      setStatus('error');
    }
  };

  return (
    <div className="dz-cta__form-card" id={idPrefix === 'cta' ? undefined : 'inquiry'}>
      <span className="dz-cta__form-eyebrow">{f.eyebrow}</span>
      {status === 'success' ? (
        <div className="dz-cta__success" role="status" aria-live="polite">
          <p className="dz-cta__success-title">{f.successTitle}</p>
          <p className="dz-cta__success-body">{f.successBody}</p>
        </div>
      ) : (
      <form className="dz-cta__form" name="dz-inquiry" method="POST" data-netlify="true" netlify-honeypot="bot-field" onSubmit={handleSubmit}>
        <input type="hidden" name="form-name" value="dz-inquiry" />
        <p hidden aria-hidden="true" style={{ display: 'none' }}>
          <label>Leave this empty: <input name="bot-field" tabIndex="-1" autoComplete="off" /></label>
        </p>
        <div className="dz-cta__field">
          <label htmlFor={id('name')}>{f.fields.name.label}</label>
          <input id={id('name')} name="name" type="text" autoComplete="name" required placeholder={f.fields.name.placeholder} />
        </div>
        <div className="dz-cta__field">
          <label htmlFor={id('email')}>{f.fields.email.label}</label>
          <input id={id('email')} name="email" type="email" autoComplete="email" required placeholder={f.fields.email.placeholder} />
        </div>
        <div className="dz-cta__field">
          <label htmlFor={id('phone')}>{f.fields.phone.label}</label>
          <input id={id('phone')} name="phone" type="tel" autoComplete="tel" dir="ltr" required placeholder={f.fields.phone.placeholder} />
        </div>
        <div className="dz-cta__field">
          <label htmlFor={id('company')}>{f.fields.company.label}</label>
          <input id={id('company')} name="company" type="text" autoComplete="organization" placeholder={f.fields.company.placeholder} />
        </div>
        <div className="dz-cta__field dz-cta__field--full">
          <label htmlFor={id('type')}>{f.fields.type.label}</label>
          <select id={id('type')} name="project_type" defaultValue={preset >= 0 ? f.projectTypes[preset] : ''}>
            <option value="" disabled>{f.fields.type.placeholderOption}</option>
            {f.projectTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="dz-cta__field dz-cta__field--full">
          <label htmlFor={id('msg')}>{f.fields.message.label}</label>
          <textarea id={id('msg')} name="message" placeholder={f.fields.message.placeholder}></textarea>
        </div>
        {status === 'error' && (
          <p className="dz-cta__error dz-cta__field--full" role="alert">{f.errorMsg}</p>
        )}
        <div className="dz-cta__form-foot">
          <span className="dz-cta__foot-note">{CTA_LOCK} {f.footNote}</span>
          <button type="submit" className="dz-cta__submit" disabled={status === 'submitting'} aria-busy={status === 'submitting'}>
            {status === 'submitting' ? f.submittingLabel : f.submitLabel} {CTA_ARROW}
          </button>
        </div>
      </form>
      )}
    </div>
  );
}

function CtaStrip() {
  const stepsRef = useRefCta(null);
  const data = ctaData();

  // Trigger the vertical-step animation when the steps area enters viewport.
  // Fire once and stay — removing the class on scroll-out caused the steps
  // to disappear when a user scrolled past, leaving an empty navy void
  // visible in mobile fullPage screenshots and on scroll-back.
  useEffectCta(() => {
    const el = stepsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('dz-cta__steps--in');
          obs.unobserve(el);
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="dz-cta" id="inquiry" aria-label={data.sectionAriaLabel}>
      <div className="dz-cta__inner">

        {/* Centered title */}
        <div className="dz-cta__head">
          <span className="dz-eyebrow">{data.eyebrow}</span>
          <h2 className="dz-cta__title">{data.titleLeading} <em>{data.titleAccent}</em></h2>
          <p className="dz-cta__lead">{data.lead}</p>
        </div>

        {/* Body: data left, form right */}
        <div className="dz-cta__body">

          {/* LEFT — important data */}
          <div className="dz-cta__data">
            <div>
              <span className="dz-cta__data-eyebrow">{data.stepsLabel}</span>
              <ol className="dz-cta__steps" ref={stepsRef}>
                {data.steps.map((step) => (
                  <li key={step.num} className="dz-cta__step">
                    <span className="dz-cta__step-num">{step.num}</span>
                    <div className="dz-cta__step-text">
                      {/* Use <h3> here, not <h4>. Lighthouse a11y audit
                          flagged a heading-order skip — pages have <h1>
                          in the hero and <h2> for section titles, so
                          <h3> is the correct next level for step
                          headings inside CtaStrip. */}
                      <h3 className="dz-cta__step-h">{step.heading}</h3>
                      <p className="dz-cta__step-sub">{step.sub}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="dz-cta__stats">
              {data.stats.map((stat, i) => (
                <div key={i} className="dz-cta__stat">
                  <span className="dz-cta__stat-num">
                    {stat.num}{stat.numSmall && <small>{stat.numSmall}</small>}
                  </span>
                  <span className="dz-cta__stat-label">{stat.label}</span>
                </div>
              ))}
            </div>

          </div>

          {/* RIGHT — inquiry form */}
          <DzInquiryForm idPrefix="cta" />

        </div>
      </div>
    </section>
  );
}

window.CtaStrip = CtaStrip;
window.DzInquiryForm = DzInquiryForm;
